import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { AppModalFooter, AppModalShell } from "../../components/app-ui.js";
import { Button } from "../../components/legacy-ui.js";
import { parseEsVeNumber, formatCurrency } from "../../utils/formatters.js";
import { normalizeDiscountScope, splitDayDiscount } from "../../utils/milkUtils.js";
import toast from "react-hot-toast";

const fmtMoney = (value) =>
  `$${Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const fmtLiters = (value) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtRate = (value) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function litersToInput(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return "";
  return String(n);
}

function parseQty(raw) {
  const typed = String(raw || "").trim();
  if (!typed) return 0;
  const n = parseEsVeNumber(typed);
  if (n == null || n < 0) return null;
  return n;
}

function paymentUsd(pay) {
  if (Number.isFinite(pay?.amountInUSD)) return pay.amountInUSD;
  if (Number.isFinite(pay?.conversionDetails?.amountInUSD)) {
    return pay.conversionDetails.amountInUSD;
  }
  if (pay?.currency === "BS") return 0;
  return Number(pay?.amount) || 0;
}

function emptyDaily(days) {
  const daily = {};
  days.forEach((day) => {
    daily[day.date] = { vaca: "", bufalo: "", discount: "", discountScope: "general" };
  });
  return daily;
}

function normalizeTrips(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((trip) => ({
      vaca: Math.round(Math.max(0, Number(trip?.vaca) || 0) * 100) / 100,
      bufalo: Math.round(Math.max(0, Number(trip?.bufalo) || 0) * 100) / 100,
    }))
    .filter((trip) => trip.vaca > 0 || trip.bufalo > 0);
}

function tripKey(trips) {
  return normalizeTrips(trips)
    .map((trip) => `${trip.vaca}|${trip.bufalo}`)
    .join(";");
}

function sumTrips(trips, field) {
  return normalizeTrips(trips).reduce((sum, trip) => sum + (trip[field] || 0), 0);
}

function tripButtonLabel(count) {
  if (count === 1) return "1 viaje";
  if (count > 1) return `${count} viajes`;
  return "Viajes";
}

function tripsFromProvider(provider, days) {
  const map = {};
  days.forEach((day) => {
    map[day.date] = normalizeTrips(provider?.daily?.[day.date]?.trips);
  });
  return map;
}

function dailyFromProvider(provider, days) {
  const daily = emptyDaily(days);
  days.forEach((day) => {
    const trips = normalizeTrips(provider?.daily?.[day.date]?.trips);
    const vaca = trips.length ? sumTrips(trips, "vaca") : provider?.daily?.[day.date]?.vaca;
    const bufalo = trips.length ? sumTrips(trips, "bufalo") : provider?.daily?.[day.date]?.bufalo;
    daily[day.date] = {
      vaca: litersToInput(vaca),
      bufalo: litersToInput(bufalo),
      discount: litersToInput(provider?.daily?.[day.date]?.discount),
      discountScope: normalizeDiscountScope(provider?.daily?.[day.date]?.discountScope),
    };
  });
  return daily;
}

function DayTripsEditor({ day, initialTrips, onClose, onApply }) {
  const [rows, setRows] = useState(() => {
    const source = initialTrips.length ? initialTrips : [{ vaca: "", bufalo: "" }];
    return source.map((trip) => ({
      vaca: trip.vaca ? String(trip.vaca) : "",
      bufalo: trip.bufalo ? String(trip.bufalo) : "",
    }));
  });
  const [error, setError] = useState("");

  const totals = rows.reduce(
    (acc, row) => ({
      vaca: acc.vaca + (parseQty(row.vaca) || 0),
      bufalo: acc.bufalo + (parseQty(row.bufalo) || 0),
    }),
    { vaca: 0, bufalo: 0 },
  );

  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    setError("");
  };

  const apply = (trips) => {
    if (trips) {
      for (let i = 0; i < rows.length; i += 1) {
        const vacaRaw = String(rows[i].vaca || "").trim();
        const bufaloRaw = String(rows[i].bufalo || "").trim();
        if (vacaRaw && parseQty(vacaRaw) == null) {
          setError(`Número inválido en vaca, viaje ${i + 1}.`);
          return;
        }
        if (bufaloRaw && parseQty(bufaloRaw) == null) {
          setError(`Número inválido en búfala, viaje ${i + 1}.`);
          return;
        }
      }
      const cleaned = [];
      rows.forEach((row) => {
        const vaca = parseQty(row.vaca) || 0;
        const bufalo = parseQty(row.bufalo) || 0;
        if (vaca <= 0 && bufalo <= 0) return;
        cleaned.push({
          vaca: Math.round(vaca * 100) / 100,
          bufalo: Math.round(bufalo * 100) / 100,
        });
      });
      onApply(cleaned);
      return;
    }
    onApply([]);
  };

  const dayLabel = `${day.shortLabel || day.label || ""} ${format(new Date(`${day.date}T12:00:00`), "dd/MM")}`.trim();

  return (
    <AppModalShell
      open
      onClose={onClose}
      title={`Viajes — ${dayLabel}`}
      widthClass="max-w-md w-full"
      fitContent
      zClass="z-[90]"
      footer={
        <AppModalFooter>
          <Button variant="secondary" onClick={() => apply(null)}>
            Quitar viajes
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={() => apply(true)}>Usar totales</Button>
          </div>
        </AppModalFooter>
      }
    >
      <p className="text-sm text-gray-500 mb-3">
        Opcional. Anota cada viaje de vaca y búfala. Al aceptar, ese día de la tabla muestra la suma.
      </p>
      <div className="grid grid-cols-[2.5rem_1fr_1fr_auto] gap-2 items-center text-xs font-semibold text-gray-500 mb-1">
        <span>Viaje</span>
        <span className="text-center">Vaca</span>
        <span className="text-center">Búfala</span>
        <span />
      </div>
      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-[2.5rem_1fr_1fr_auto] gap-2 items-center">
            <span className="text-sm text-center text-gray-600">{index + 1}</span>
            <input
              type="text"
              inputMode="decimal"
              className="w-full text-center text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600"
              value={row.vaca}
              onChange={(e) => updateRow(index, "vaca", e.target.value)}
            />
            <input
              type="text"
              inputMode="decimal"
              className="w-full text-center text-sm bg-white border border-gray-300 rounded px-2 py-1.5 outline-none focus:border-emerald-600"
              value={row.bufalo}
              onChange={(e) => updateRow(index, "bufalo", e.target.value)}
            />
            <button
              type="button"
              className="text-xs text-red-600 px-1 py-1"
              onClick={() =>
                setRows((prev) => (prev.length === 1 ? [{ vaca: "", bufalo: "" }] : prev.filter((_, i) => i !== index)))
              }
            >
              Quitar
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className="mt-3 text-sm font-semibold text-emerald-700"
        onClick={() => setRows((prev) => [...prev, { vaca: "", bufalo: "" }])}
      >
        Agregar viaje
      </button>
      <p className="mt-3 text-sm text-gray-700">
        Total del día: vaca {fmtLiters(totals.vaca)} L · búfala {fmtLiters(totals.bufalo)} L
      </p>
      {error ? <p className="mt-2 text-sm text-red-600">{error}</p> : null}
    </AppModalShell>
  );
}

function pricesFromProvider(provider) {
  return {
    priceCow: provider?.priceCow ? String(provider.priceCow) : "",
    priceBuffalo: provider?.priceBuffalo ? String(provider.priceBuffalo) : "",
  };
}

export default forwardRef(function WeeklyMilkSheet({
  days = [],
  provider,
  weekPayments = [],
  onSaveWeek,
  onSavingChange,
  onRegisterPayment,
  onDeletePayment,
  hidePrices = false,
}, ref) {
  const [daily, setDaily] = useState(() => dailyFromProvider(provider, days));
  const [dayTrips, setDayTrips] = useState(() => tripsFromProvider(provider, days));
  const [tripDay, setTripDay] = useState(null);
  const [priceCow, setPriceCow] = useState("");
  const [priceBuffalo, setPriceBuffalo] = useState("");
  const [selectedPaymentId, setSelectedPaymentId] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });
  const skipAutoSave = useRef(true);
  const savingRef = useRef(false);
  const dirtyRef = useRef(false);
  const handleSaveRef = useRef(null);

  const weekStartKey = days[0]?.date;

  useEffect(() => {
    onSavingChange?.(saving);
  }, [saving, onSavingChange]);

  useEffect(() => {
    setDaily(dailyFromProvider(provider, days));
    setDayTrips(tripsFromProvider(provider, days));
    setTripDay(null);
    const prices = pricesFromProvider(provider);
    setPriceCow(prices.priceCow);
    setPriceBuffalo(prices.priceBuffalo);
    setStatus({ type: "", text: "" });
    skipAutoSave.current = true;
    // Only reset when switching provider/week; parent remounts via key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider?.id, weekStartKey]);

  const setCell = (dateKey, milkType, value) => {
    if ((milkType === "vaca" || milkType === "bufalo") && dayTrips[dateKey]?.length) return;
    setDaily((prev) => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || { vaca: "", bufalo: "", discount: "", discountScope: "general" }),
        [milkType]: value,
      },
    }));
    setStatus({ type: "", text: "" });
  };

  const applyDayTrips = (dateKey, trips) => {
    const cleaned = normalizeTrips(trips);
    setDayTrips((prev) => ({ ...prev, [dateKey]: cleaned }));
    if (cleaned.length) {
      setDaily((prev) => ({
        ...prev,
        [dateKey]: {
          ...(prev[dateKey] || { vaca: "", bufalo: "", discount: "", discountScope: "general" }),
          vaca: litersToInput(sumTrips(cleaned, "vaca")),
          bufalo: litersToInput(sumTrips(cleaned, "bufalo")),
        },
      }));
    }
    setStatus({ type: "", text: "" });
    setTripDay(null);
  };

  const preview = useMemo(() => {
    const dailyVaca = {};
    const dailyBufalo = {};
    const dailyDiscount = {};
    const dailyNet = {};
    let litersVaca = 0;
    let litersBufalo = 0;
    let grossVaca = 0;
    let grossBufalo = 0;
    let discountLiters = 0;
    days.forEach((day) => {
      const vaca = parseQty(daily[day.date]?.vaca) || 0;
      const bufalo = parseQty(daily[day.date]?.bufalo) || 0;
      const discount = parseQty(daily[day.date]?.discount) || 0;
      const scope = normalizeDiscountScope(daily[day.date]?.discountScope);
      const split = splitDayDiscount(vaca, bufalo, discount, scope);
      dailyVaca[day.date] = vaca;
      dailyBufalo[day.date] = bufalo;
      dailyDiscount[day.date] = split.discount;
      dailyNet[day.date] = split.netVaca + split.netBufalo;
      litersVaca += split.netVaca;
      litersBufalo += split.netBufalo;
      grossVaca += vaca;
      grossBufalo += bufalo;
      discountLiters += split.discount;
    });
    const cowPrice = parseEsVeNumber(priceCow) ?? Number(provider?.priceCow || 0);
    const buffaloPrice = parseEsVeNumber(priceBuffalo) ?? Number(provider?.priceBuffalo || 0);
    const owedVaca = litersVaca * cowPrice;
    const owedBufalo = litersBufalo * buffaloPrice;
    const totalOwed = owedVaca + owedBufalo;
    const grossOwed =
      Object.keys(dailyVaca).reduce((acc, key) => acc + (dailyVaca[key] || 0), 0) * cowPrice +
      Object.keys(dailyBufalo).reduce((acc, key) => acc + (dailyBufalo[key] || 0), 0) * buffaloPrice;
    return {
      dailyVaca,
      dailyBufalo,
      dailyDiscount,
      dailyNet,
      litersVaca,
      litersBufalo,
      grossVaca,
      grossBufalo,
      discountLiters,
      discountAmount: Math.max(0, grossOwed - totalOwed),
      priceCow: cowPrice,
      priceBuffalo: buffaloPrice,
      owedVaca,
      owedBufalo,
      totalOwed,
      pendingAmount: totalOwed - Number(provider?.totalPaid || 0),
    };
  }, [daily, days, priceCow, priceBuffalo, provider?.priceCow, provider?.priceBuffalo, provider?.totalPaid]);

  const handleSave = useCallback(async ({ source = "manual" } = {}) => {
    if (!provider) return;
    const silent = source === "auto";
    if (savingRef.current) {
      dirtyRef.current = true;
      return;
    }

    const cells = [];
    const tripUpdates = [];
    for (const day of days) {
      const vacaRaw = daily[day.date]?.vaca;
      const bufaloRaw = daily[day.date]?.bufalo;
      const discountRaw = daily[day.date]?.discount;
      const vacaTyped = String(vacaRaw || "").trim();
      const bufaloTyped = String(bufaloRaw || "").trim();
      const discountTyped = String(discountRaw || "").trim();
      if (vacaTyped && parseQty(vacaRaw) == null) {
        if (silent) return;
        setStatus({ type: "error", text: `Número inválido en vaca (${day.shortLabel || day.label}).` });
        return;
      }
      if (bufaloTyped && parseQty(bufaloRaw) == null) {
        if (silent) return;
        setStatus({ type: "error", text: `Número inválido en búfala (${day.shortLabel || day.label}).` });
        return;
      }
      if (discountTyped && parseQty(discountRaw) == null) {
        if (silent) return;
        setStatus({ type: "error", text: `Descuento inválido (${day.shortLabel || day.label}).` });
        return;
      }
      const vaca = parseQty(vacaRaw) || 0;
      const bufalo = parseQty(bufaloRaw) || 0;
      const discount = parseQty(discountRaw) || 0;
      const scope = normalizeDiscountScope(daily[day.date]?.discountScope);
      const cap = scope === "vaca" ? vaca : scope === "bufalo" ? bufalo : vaca + bufalo;
      const capLabel =
        scope === "vaca" ? "los litros de vaca" : scope === "bufalo" ? "los litros de búfala" : "los litros del día";
      if (discount > cap + 0.0001) {
        if (silent) return;
        setStatus({
          type: "error",
          text: `El descuento no puede ser mayor que ${capLabel} (${day.shortLabel || day.label}).`,
        });
        return;
      }
      const split = splitDayDiscount(vaca, bufalo, discount, scope);
      const current = provider?.daily?.[day.date] || {};
      const currentVaca = Number(current.vaca || 0);
      const currentBufalo = Number(current.bufalo || 0);
      const currentDiscount = Number(current.discount || 0);
      const currentScope = normalizeDiscountScope(current.discountScope);
      const dayChanged =
        vaca !== currentVaca ||
        bufalo !== currentBufalo ||
        split.discount !== currentDiscount ||
        split.scope !== currentScope;
      const savedTrips = normalizeTrips(provider?.daily?.[day.date]?.trips);
      const nextTrips = normalizeTrips(dayTrips[day.date]);
      if (tripKey(savedTrips) !== tripKey(nextTrips)) {
        tripUpdates.push({ date: day.date, trips: nextTrips });
      }
      if (!dayChanged) continue;
      cells.push({
        date: day.date,
        milkType: "vaca",
        liters: vaca,
        discount: split.vacaDisc,
        discountScope: split.scope,
      });
      cells.push({
        date: day.date,
        milkType: "bufalo",
        liters: bufalo,
        discount: split.bufaloDisc,
        discountScope: split.scope,
      });
    }

    const prices = {};
    if (!hidePrices) {
      const nextCow = parseEsVeNumber(priceCow);
      const nextBuffalo = parseEsVeNumber(priceBuffalo);
      if (String(priceCow).trim() && nextCow == null) {
        if (silent) return;
        setStatus({ type: "error", text: "Precio de vaca inválido." });
        return;
      }
      if (String(priceBuffalo).trim() && nextBuffalo == null) {
        if (silent) return;
        setStatus({ type: "error", text: "Precio de búfala inválido." });
        return;
      }
      if ((nextCow ?? 0) !== Number(provider?.priceCow || 0)) prices.priceCow = nextCow ?? 0;
      if ((nextBuffalo ?? 0) !== Number(provider?.priceBuffalo || 0)) {
        prices.priceBuffalo = nextBuffalo ?? 0;
      }
    }

    if (cells.length === 0 && Object.keys(prices).length === 0 && tripUpdates.length === 0) {
      if (!silent) setStatus({ type: "saved", text: "No hay cambios que guardar." });
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setStatus({ type: "saving", text: "Guardando…" });
    try {
      await onSaveWeek({ cells, prices, trips: tripUpdates });
      setStatus({ type: "saved", text: silent ? "Guardado" : "Guardado correctamente." });
      if (!silent) {
        toast.success("Semana guardada", { position: "top-center" });
      }
    } catch (err) {
      console.error(err);
      const message =
        err?.response?.data?.error || err?.message || "No se pudo guardar. Intenta de nuevo.";
      setStatus({
        type: "error",
        text: message,
      });
      if (!silent) {
        toast.error(message, { position: "top-center" });
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
      if (dirtyRef.current) {
        dirtyRef.current = false;
        handleSaveRef.current?.({ source: source });
      }
    }
  }, [provider, days, daily, dayTrips, priceCow, priceBuffalo, onSaveWeek, hidePrices]);

  handleSaveRef.current = handleSave;

  useEffect(() => {
    if (skipAutoSave.current) {
      skipAutoSave.current = false;
      return undefined;
    }
    const timer = setTimeout(() => {
      handleSaveRef.current?.({ source: "auto" });
    }, 800);
    return () => clearTimeout(timer);
  }, [daily, dayTrips, priceCow, priceBuffalo]);

  const getExportSnapshot = useCallback(() => {
    const snapshotDaily = {};
    days.forEach((day) => {
      const cell = daily[day.date] || {};
      snapshotDaily[day.date] = {
        vaca: parseQty(cell.vaca) || 0,
        bufalo: parseQty(cell.bufalo) || 0,
        discount: parseQty(cell.discount) || 0,
        discountScope: normalizeDiscountScope(cell.discountScope),
      };
    });
    return {
      daily: snapshotDaily,
      prices: {
        priceCow: parseQty(priceCow) || 0,
        priceBuffalo: parseQty(priceBuffalo) || 0,
      },
    };
  }, [days, daily, priceCow, priceBuffalo]);

  useImperativeHandle(
    ref,
    () => ({ save: handleSave, getExportSnapshot }),
    [handleSave, getExportSnapshot],
  );

  if (!provider) return null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Anota los litros diarios (vaca / búfala). Si quieres, abre Viajes en un día y carga cada recibo de vaca y búfala; la tabla usa el total. Si la leche trae agua, registra el descuento y elige si aplica a vaca, búfala o a las dos. El cierre es el miércoles.
      </p>

      <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
        <table className="w-full text-sm min-w-[720px]">
          <thead>
            <tr className="bg-[#e7efe9] text-gray-800">
              <th className="px-2 py-2 text-left font-semibold w-20" />
              {days.map((day) => (
                <th key={day.date} className="px-1 py-2 text-center font-semibold min-w-[5rem]">
                  <div className="whitespace-pre-line leading-tight">
                    {day.shortLabel || day.label}
                    {"\n"}
                    {format(new Date(`${day.date}T12:00:00`), "dd/MM")}
                  </div>
                </th>
              ))}
              <th className="px-2 py-2 text-right font-semibold whitespace-nowrap">Total L</th>
            </tr>
          </thead>
          <tbody>
            {["vaca", "bufalo"].map((milkType) => {
              const isCow = milkType === "vaca";
              return (
                <tr key={milkType} className="border-t border-gray-100">
                  <td className="px-2 py-2 font-medium text-gray-800 whitespace-nowrap">
                    {isCow ? "Vaca" : "Búfala"}
                  </td>
                  {days.map((day) => {
                    const locked = Boolean(dayTrips[day.date]?.length);
                    return (
                    <td key={`${milkType}-${day.date}`} className="px-1 py-1.5">
                      <input
                        type="text"
                        inputMode="decimal"
                        readOnly={locked}
                        title={locked ? "Total de los viajes de este día" : undefined}
                        className={`w-full min-w-[4.75rem] text-center text-sm border rounded px-1 py-1 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30 ${
                          locked
                            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                            : "bg-white border-gray-300"
                        }`}
                        value={daily[day.date]?.[milkType] ?? ""}
                        onChange={(e) => setCell(day.date, milkType, e.target.value)}
                      />
                    </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right font-semibold tabular-nums whitespace-nowrap">
                    {fmtLiters(isCow ? preview.grossVaca : preview.grossBufalo)}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t border-amber-100 bg-amber-50/50">
              <td className="px-2 py-2 font-medium text-amber-800 whitespace-nowrap">Descuento</td>
              {days.map((day) => (
                <td key={`discount-${day.date}`} className="px-1 py-1.5">
                  <input
                    type="text"
                    inputMode="decimal"
                    title="Litros a descontar por agua"
                    className="w-full min-w-[4.75rem] text-center text-sm bg-white border border-amber-200 rounded px-1 py-1 outline-none focus:border-amber-600 focus:ring-1 focus:ring-amber-600/30"
                    value={daily[day.date]?.discount ?? ""}
                    onChange={(e) => setCell(day.date, "discount", e.target.value)}
                  />
                  <select
                    title="Tipo de leche del descuento"
                    className="mt-1 w-full min-w-[4.75rem] text-center text-[11px] bg-white border border-amber-200 rounded px-0.5 py-0.5 outline-none focus:border-amber-600"
                    value={daily[day.date]?.discountScope || "general"}
                    onChange={(e) => setCell(day.date, "discountScope", e.target.value)}
                  >
                    <option value="general">General</option>
                    <option value="vaca">Vaca</option>
                    <option value="bufalo">Búfala</option>
                  </select>
                </td>
              ))}
              <td className="px-2 py-2 text-right font-semibold tabular-nums text-amber-800 whitespace-nowrap">
                {fmtLiters(preview.discountLiters)}
              </td>
            </tr>
            <tr className="border-t border-gray-200 bg-[#e7efe9]/70">
              <td className="px-2 py-2 font-semibold text-gray-900 whitespace-nowrap">Total L</td>
              {days.map((day) => (
                <td key={`total-${day.date}`} className="px-1 py-2 text-center font-semibold tabular-nums">
                  {fmtLiters(preview.dailyNet[day.date] || 0)}
                </td>
              ))}
              <td className="px-2 py-2 text-right font-semibold tabular-nums whitespace-nowrap">
                {fmtLiters(preview.litersVaca + preview.litersBufalo)}
              </td>
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-2 py-2 font-medium text-gray-800 whitespace-nowrap">Viajes</td>
              {days.map((day) => {
                const count = dayTrips[day.date]?.length || 0;
                return (
                  <td key={`trips-${day.date}`} className="px-1 py-1.5">
                    <button
                      type="button"
                      onClick={() => setTripDay(day)}
                      className={`w-full min-w-[4.75rem] text-center text-[11px] leading-tight rounded px-1 py-1 border ${
                        count
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold"
                          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      {tripButtonLabel(count)}
                    </button>
                  </td>
                );
              })}
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {!hidePrices && (
      <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
        <table className="w-full text-sm max-w-md">
          <thead>
            <tr className="bg-[#e7efe9] text-gray-800">
              <th className="px-3 py-2 text-left font-semibold"> </th>
              <th className="px-3 py-2 text-right font-semibold">Precio $/L</th>
              <th className="px-3 py-2 text-right font-semibold">$</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-800">Vaca</td>
              <td className="px-3 py-1.5">
                <input
                  type="text"
                  inputMode="decimal"
                  title="Precio vaca $/L"
                  className="w-full min-w-[5rem] text-right text-sm bg-white border border-gray-300 rounded px-2 py-1 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30"
                  value={priceCow}
                  onChange={(e) => {
                    setPriceCow(e.target.value);
                    setStatus({ type: "", text: "" });
                  }}
                />
              </td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtMoney(preview.owedVaca)}</td>
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2 font-medium text-gray-800">Búfala</td>
              <td className="px-3 py-1.5">
                <input
                  type="text"
                  inputMode="decimal"
                  title="Precio búfala $/L"
                  className="w-full min-w-[5rem] text-right text-sm bg-white border border-gray-300 rounded px-2 py-1 outline-none focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/30"
                  value={priceBuffalo}
                  onChange={(e) => {
                    setPriceBuffalo(e.target.value);
                    setStatus({ type: "", text: "" });
                  }}
                />
              </td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtMoney(preview.owedBufalo)}</td>
            </tr>
            <tr className="border-t border-amber-100 bg-amber-50/50">
              <td className="px-3 py-2 font-medium text-amber-800">Descuento</td>
              <td className="px-3 py-2 text-right text-xs font-medium text-amber-700">Agua</td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums text-amber-800">
                {preview.discountAmount > 0 ? `−${fmtMoney(preview.discountAmount)}` : fmtMoney(0)}
              </td>
            </tr>
            <tr className="border-t border-gray-200 bg-[#e7efe9]/70">
              <td className="px-3 py-2 font-semibold text-gray-900">Total</td>
              <td className="px-3 py-2 text-right text-xs font-medium text-gray-600">Total $</td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtMoney(preview.totalOwed)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      )}

      <div className="flex flex-col gap-2">
        {!hidePrices && (
          <p className="text-sm text-gray-600">
            Pendiente: {fmtMoney(preview.pendingAmount)}
          </p>
        )}
        {status.text ? (
          <span
            className={
              status.type === "error"
                ? "text-sm text-red-600"
                : status.type === "saved"
                  ? "text-sm text-emerald-700"
                  : "text-sm text-gray-600"
            }
          >
            {status.text}
          </span>
        ) : (
          <span className="text-sm text-gray-400">Se guarda automáticamente al anotar los litros.</span>
        )}
      </div>

      {!hidePrices && <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-gray-800">Abonos de la semana</h3>
          <Button onClick={onRegisterPayment}>
            Registrar abono
          </Button>
        </div>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-[#e7efe9] text-gray-700">
              <tr>
                <th className="px-3 py-2 text-right font-semibold">$</th>
                <th className="px-3 py-2 text-right font-semibold">BCV</th>
                <th className="px-3 py-2 text-right font-semibold">Bs</th>
                <th className="px-3 py-2 text-left font-semibold">Código</th>
                <th className="px-3 py-2 text-center font-semibold">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {weekPayments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-400">
                    No hay abonos esta semana.
                  </td>
                </tr>
              ) : (
                weekPayments.map((pay) => {
                  const usd = paymentUsd(pay);
                  const bcv = pay.conversionDetails?.exchangeRate;
                  const isBs = pay.currency === "BS";
                  const selected = selectedPaymentId === pay._id;
                  return (
                    <tr
                      key={pay._id}
                      onClick={() => setSelectedPaymentId(pay._id)}
                      className={`border-t border-gray-100 cursor-pointer ${
                        selected ? "bg-[#d4ebdd]" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-3 py-2 text-right font-medium">{fmtMoney(usd)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">
                        {bcv ? fmtRate(bcv) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600">
                        {isBs ? formatCurrency(pay.amount, "BS") : "—"}
                      </td>
                      <td className="px-3 py-2">{pay.reference || "—"}</td>
                      <td className="px-3 py-2 text-center text-gray-600">
                        {format(new Date(pay.paymentDate || pay.createdAt), "dd/MM/yyyy")}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-start mt-3">
          <Button
            variant="secondary"
            disabled={!selectedPaymentId}
            onClick={() => {
              if (!selectedPaymentId) return;
              onDeletePayment(selectedPaymentId);
              setSelectedPaymentId("");
            }}
          >
            Eliminar abono
          </Button>
        </div>
      </div>}
      {tripDay ? (
        <DayTripsEditor
          day={tripDay}
          initialTrips={dayTrips[tripDay.date] || []}
          onClose={() => setTripDay(null)}
          onApply={(trips) => applyDayTrips(tripDay.date, trips)}
        />
      ) : null}
    </div>
  );
});
